import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Divider, Spin } from 'antd';
import {
  CheckCircleFilled, CloseCircleFilled, WarningFilled,
  CarOutlined, UserOutlined, CalendarOutlined,
} from '@ant-design/icons';
import { verifierParToken } from '../../services/attestationDigitaleService';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

const STATUTS = {
  VALIDE: { icon: <CheckCircleFilled style={{ fontSize: 64, color: '#16a34a' }} />, color: '#16a34a', bg: '#f0fdf4' },
  EXPIREE: { icon: <WarningFilled style={{ fontSize: 64, color: '#d97706' }} />, color: '#d97706', bg: '#fffbeb' },
  ANNULEE: { icon: <CloseCircleFilled style={{ fontSize: 64, color: '#dc2626' }} />, color: '#dc2626', bg: '#fef2f2' },
  SUSPENDUE: { icon: <WarningFilled style={{ fontSize: 64, color: '#7c3aed' }} />, color: '#7c3aed', bg: '#f5f3ff' },
  INVALIDE: { icon: <CloseCircleFilled style={{ fontSize: 64, color: '#dc2626' }} />, color: '#dc2626', bg: '#fef2f2' },
  INTROUVABLE: { icon: <CloseCircleFilled style={{ fontSize: 64, color: '#6b7280' }} />, color: '#6b7280', bg: '#f9fafb' },
};

export default function PortailVerification() {
  const { t } = useTranslation('verification');
  const { token } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    verifierParToken(token)
      .then(setResult)
      .catch(() => setError(i18n.t('verification:portail.introuvableOuTokenInvalide')))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#6b7280' }}>{t('portail.verificationEnCours')}</p>
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <CloseCircleFilled style={{ fontSize: 64, color: '#dc2626' }} />
          <h2 style={{ marginTop: 16, color: '#dc2626' }}>{t('portail.introuvable')}</h2>
          <p style={{ color: '#6b7280' }}>{error}</p>
        </div>
      </PageWrapper>
    );
  }

  if (!result) {
    return (
      <PageWrapper>
        <Alert type="info" message={t('portail.aucuneAttestation')} />
      </PageWrapper>
    );
  }

  const statutInfo = STATUTS[result.resultat] || { icon: null, label: result.resultat, color: '#374151', bg: '#f9fafb' };

  return (
    <PageWrapper>
      <div style={{
        maxWidth: 520, margin: '0 auto', background: '#fff',
        borderRadius: 16, overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      }}>
        {/* Status header */}
        <div style={{
          background: statutInfo.bg, padding: '32px 24px', textAlign: 'center',
          borderBottom: `3px solid ${statutInfo.color}`,
        }}>
          {statutInfo.icon}
          <h2 style={{ margin: '12px 0 4px', color: statutInfo.color, fontSize: 28, fontWeight: 800 }}>
            {STATUTS[result.resultat] ? t(`statuts.${result.resultat}`) : statutInfo.label}
          </h2>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {t('portail.attestationCarteBrune')}
          </div>
        </div>

        {/* Attestation info */}
        <div style={{ padding: '24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('portail.numeroAttestation')}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0d1b3e', letterSpacing: 1.5 }}>
              {result.numeroAttestation}
            </div>
          </div>

          <InfoRow icon={<UserOutlined />} label={t('portail.souscripteur')}
            value={`${result.souscripteurNom || ''} ${result.souscripteurPrenom || ''}`.trim()} />

          <InfoRow icon={<CarOutlined />} label={t('portail.vehicule')}
            value={result.vehiculeImmatriculation}
            sub={`${result.vehiculeMarque || ''} ${result.vehiculeModele || ''}`.trim()} />

          <Divider style={{ margin: '12px 0' }} />

          <InfoRow icon={<CalendarOutlined />} label={t('portail.periodeCouverture')}
            value={`${result.dateEffet || '-'} → ${result.dateEcheance || '-'}`} />

          <InfoRow label={t('portail.compagnie')} value={result.compagnieRaisonSociale || '-'} />

          {result.resultat === 'VALIDE' && (
            <div style={{
              marginTop: 16, padding: '10px 16px', background: '#f0fdf4',
              borderRadius: 8, border: '1px solid #bbf7d0',
              textAlign: 'center', fontSize: 13, color: '#166534',
            }}>
              {t('portail.vehiculeAssure')}
            </div>
          )}

          {result.resultat !== 'VALIDE' && result.messageAffichage && (
            <div style={{
              marginTop: 16, padding: '10px 16px', background: '#fef2f2',
              borderRadius: 8, border: '1px solid #fecaca',
              fontSize: 13, color: '#991b1b',
            }}>
              {result.messageAffichage}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', background: '#0d1b3e',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('portail.emisPar')}
            </div>
            <div style={{ color: '#d4a044', fontSize: 12, fontWeight: 600 }}>
              {t('portail.bureau')}
            </div>
          </div>
          <div style={{
            fontSize: 11, color: '#009b4e', fontWeight: 700,
            border: '1px solid #009b4e', borderRadius: 4, padding: '2px 8px',
          }}>
            CEDEAO / ECOWAS
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
          {t('portail.verifieLe', { date: new Date().toLocaleString(localeIntl()) })}
        </p>
      </div>
    </PageWrapper>
  );
}

function PageWrapper({ children }) {
  const { t } = useTranslation('verification');
  return (
    <div style={{
      minHeight: 'calc(100vh - var(--cb-banniere-h, 0px))',
      background: 'linear-gradient(135deg, #0d1b3e 0%, #163d28 50%, #1a6b3c 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{ marginBottom: 24, textAlign: 'center' }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 20 }}>CBConnect</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{t('portail.sousTitre')}</div>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
      {icon && (
        <span style={{ color: '#1a6b3c', marginTop: 2, flexShrink: 0 }}>{icon}</span>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{value || '-'}</div>
        {sub && <div style={{ fontSize: 12, color: '#6b7280' }}>{sub}</div>}
      </div>
    </div>
  );
}
